import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Loader2 } from 'lucide-react';
import { useAskSwirlStore } from '../../stores/askSwirlStore';
import { useAuth } from '../../contexts/AuthContext';
import contentService from '../../services/contentService';
import topicService from '../../services/topicService';
import deepsearchService from '../../services/deepsearchService';
import focusService from '../../services/focusService';
import { supabase } from '../../lib/supabase';
import { getImageUrl } from '../../utils/imageProxy';

/**
 * Convert a storage path or partial URL to a full public Supabase Storage URL
 */
function getFullStorageUrl(url, bucket = 'user_uploads') {
  if (!url) return null;
  
  // If it's already a full URL, return it
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // If it's a data URL, return it
  if (url.startsWith('data:')) {
    return url;
  }
  
  // If it's a gradient, return it
  if (url.startsWith('linear-gradient')) {
    return url;
  }
  
  // Try to get public URL from storage
  try {
    // Handle different path formats
    let storagePath = url;
    
    // If it's just a filename like "1234-file.png", try to find it in user_uploads
    if (!storagePath.includes('/') && !storagePath.startsWith('uploads/')) {
      // This is likely incomplete - we need the full path
      // For now, return null and log a warning
      console.warn('Incomplete storage path detected:', url);
      return null;
    }
    
    // Remove leading slash if present
    if (storagePath.startsWith('/')) {
      storagePath = storagePath.slice(1);
    }
    
    // Remove 'uploads/' prefix if present (it's added by getPublicUrl)
    if (storagePath.startsWith('uploads/')) {
      storagePath = storagePath.replace('uploads/', '');
    }
    
    const { data } = supabase.storage.from(bucket).getPublicUrl(storagePath);
    return data.publicUrl;
  } catch (error) {
    console.warn('Failed to get public URL for:', url, error);
    return null;
  }
}

/**
 * Context Selector Popup
 * Floating popup for selecting items, collections, focus, tags, or recent items
 */
export default function SwirlContextSelector() {
  const { 
    isSelectorOpen, 
    closeSelector, 
    selectorTab, 
    setSelectorTab,
    selectorSelected,
    toggleSelectorItem,
    confirmSelectorSelection
  } = useAskSwirlStore();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [collections, setCollections] = useState([]);
  const [focusBoards, setFocusBoards] = useState([]);
  const [docs, setDocs] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const popupRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isSelectorOpen) {
        closeSelector();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSelectorOpen, closeSelector]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        closeSelector();
      }
    };
    if (isSelectorOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSelectorOpen, closeSelector]);

  // Load data based on active tab
  useEffect(() => {
    if (!isSelectorOpen || !user?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        switch (selectorTab) {
          case 'items':
            await loadItems();
            break;
          case 'collections':
            await loadCollections();
            break;
          case 'focus':
            await loadFocusBoards();
            break;
          case 'docs':
            await loadDocs();
            break;
          case 'recent':
            await loadRecentItems();
            break;
        }
      } catch (error) {
        console.error('Error loading selector data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isSelectorOpen, selectorTab, user?.id]);

  const loadItems = async () => {
    try {
      const result = await deepsearchService.getItems(user.id);
      const data = result?.data || result || [];
      
      const formatted = (Array.isArray(data) ? data : []).slice(0, 50).map(item => {
        const actualSourceType = item.source_type || 'image';
        
        // Use the EXACT same thumbnail resolution logic as Swirlspace
        // IMPORTANT: Check item.thumbnail_url (direct column) for all types
        // The migration sets thumbnail_url column, not image_url
        let thumbnailUrl = null;
        if (actualSourceType === 'image') {
          thumbnailUrl = item.image_url || item.thumbnail_url || item.url;
        } else if (actualSourceType === 'book') {
          thumbnailUrl =
            item.image_url ||
            item.thumbnail_url ||
            item.meta?.coverUrl ||
            item.meta?.imageUrl ||
            item.meta?.thumbnail_url ||
            item.meta?.thumbnail ||
            null;
        } else if (actualSourceType === 'video') {
          thumbnailUrl =
            item.image_url ||
            item.thumbnail_url ||
            item.meta?.thumbnail_url ||
            item.metadata?.thumbnail_url ||
            item.meta?.thumbnail ||
            item.metadata?.thumbnail ||
            item.url ||
            null;
        } else {
          thumbnailUrl =
            item.image_url ||
            item.thumbnail_url ||
            item.meta?.thumbnail_url ||
            item.metadata?.thumbnail_url ||
            item.meta?.thumbnail ||
            item.metadata?.thumbnail ||
            null;
        }
        
        // Get full public URL for file
        const fileUrl = getFullStorageUrl(item.url || item.meta?.url, 'user_uploads') || item.url;
        
        // Convert thumbnail to full URL if needed
        thumbnailUrl = getFullStorageUrl(thumbnailUrl, 'user_uploads') || thumbnailUrl;
        
        // Use image proxy for external URLs (Instagram, etc.) - same as ContentCard
        if (thumbnailUrl) {
          thumbnailUrl = getImageUrl({ thumbnail_url: thumbnailUrl }) || thumbnailUrl;
        }
        
        // Extract file info
        const fileName = item.meta?.filename || item.title || item.url?.split('/').pop() || 'file';
        const fileType = item.meta?.filetype || item.source_type || 'unknown';
        const fileSize = item.meta?.filesize || null;
        
        return {
          id: item.id,
          type: 'item',
          label: item.title || item.description || 'Untitled',
          thumbnail: thumbnailUrl,
          title: item.title,
          description: item.description,
          source_type: item.source_type,
          // Store full file information
          file_url: fileUrl,
          file_name: fileName,
          file_type: fileType,
          file_size: fileSize,
          url: fileUrl, // Keep for backward compatibility
        };
      });
      
      setItems(formatted);
    } catch (error) {
      console.error('Error loading items:', error);
      setItems([]);
    }
  };

  const loadCollections = async () => {
    try {
      const { data } = await topicService.getUserTopics(user.id);
      const formatted = (data || []).map(collection => {
        // Use recent_thumbnail if available, otherwise fallback to gradient
        const thumbnail = collection.recent_thumbnail 
          ? getFullStorageUrl(collection.recent_thumbnail, 'user_uploads') || collection.recent_thumbnail
          : collection.color 
            ? `linear-gradient(135deg, ${collection.color}20, ${collection.color}40)` 
            : `linear-gradient(135deg, #ec4899, #f472b6)`;
        
        return {
          id: collection.id,
          type: 'collection',
          label: collection.name,
          thumbnail,
          name: collection.name,
          color: collection.color,
        };
      });
      setCollections(formatted);
    } catch (error) {
      console.error('Error loading collections:', error);
      setCollections([]);
    }
  };

  const loadFocusBoards = async () => {
    // Focus boards are from public.focuses table
    try {
      // focusService.getUserFocuses returns an array directly, not { data }
      const focuses = await focusService.getUserFocuses(user.id);
      const formatted = (focuses || []).map(focus => ({
        id: focus.id,
        type: 'focus',
        label: focus.title,
        thumbnail: focus.color ? `linear-gradient(135deg, ${focus.color}20, ${focus.color}40)` : `linear-gradient(135deg, #9333ea20, #9333ea40)`,
        name: focus.title,
        color: focus.color,
      }));
      setFocusBoards(formatted);
    } catch (error) {
      console.error('Error loading focus boards:', error);
      setFocusBoards([]);
    }
  };

  const loadDocs = async () => {
    try {
      // Get all documents (will be filtered by RLS)
      const { data, error } = await supabase
        .schema('public')
        .from('documents')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      
      const formatted = (data || []).map(doc => ({
        id: doc.id,
        type: 'doc',
        label: doc.title || 'Untitled',
        thumbnail: `linear-gradient(135deg, #9333ea20, #7c3aed40)`,
        title: doc.title,
        updated_at: doc.updated_at,
      }));
      setDocs(formatted);
    } catch (error) {
      console.error('Error loading docs:', error);
      setDocs([]);
    }
  };

  const loadRecentItems = async () => {
    try {
      const { data } = await contentService.getRecentContent(user.id, 20);
      const formatted = (data || []).map(item => {
        // Get full public URL for file
        const fileUrl = getFullStorageUrl(item.url || item.image_url, 'user_uploads') || item.url || item.image_url;
        const thumbnailUrl = getFullStorageUrl(item.thumbnail_url || item.image_url, 'user_uploads') || item.thumbnail_url || item.image_url;
        
        // Extract file info
        const fileName = item.file_info?.name || item.title || item.url?.split('/').pop() || 'file';
        const fileType = item.file_info?.type || item.content_type || 'unknown';
        const fileSize = item.file_info?.size || null;
        
        return {
          id: item.id,
          type: 'item',
          label: item.title || 'Untitled',
          thumbnail: thumbnailUrl,
          title: item.title,
          description: item.description,
          // Store full file information
          file_url: fileUrl,
          file_name: fileName,
          file_type: fileType,
          file_size: fileSize,
          url: fileUrl, // Keep for backward compatibility
        };
      });
      setRecentItems(formatted);
    } catch (error) {
      console.error('Error loading recent items:', error);
      setRecentItems([]);
    }
  };

  const getCurrentData = () => {
    switch (selectorTab) {
      case 'items': return items;
      case 'collections': return collections;
      case 'focus': return focusBoards;
      case 'docs': return docs;
      case 'recent': return recentItems;
      default: return [];
    }
  };

  const isSelected = (item) => {
    return selectorSelected.some(sel => sel.id === item.id);
  };

  const tabs = [
    { id: 'items', label: 'Items' },
    { id: 'collections', label: 'Collections' },
    { id: 'focus', label: 'Focus' },
    { id: 'docs', label: 'Docs' },
    { id: 'recent', label: 'Recent' },
  ];

  if (!isSelectorOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none pb-24">
        <motion.div
          ref={popupRef}
          initial={{ opacity: 0, scale: 0.92, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 20 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="w-full max-w-[640px] h-[650px] bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-neutral-200/50 pointer-events-auto flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-neutral-200/50">
            <h3 className="text-lg font-semibold text-neutral-900">Select from Swirlspace</h3>
            <button
              onClick={closeSelector}
              className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors"
            >
              <X size={18} className="text-neutral-600" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-neutral-200/50">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectorTab(tab.id)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  selectorTab === tab.id
                    ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50/50'
                    : 'text-neutral-600 hover:text-neutral-900 hover:bg-neutral-50/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 size={24} className="animate-spin text-purple-600" />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {getCurrentData().map((item) => {
                  const selected = isSelected(item);
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => toggleSelectorItem(item)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`relative p-2 rounded-lg border-2 transition-all text-left ${
                        selected
                          ? 'border-purple-500 bg-purple-50/50 shadow-md'
                          : 'border-neutral-200 hover:border-neutral-300 bg-white'
                      }`}
                    >
                      {/* Thumbnail */}
                      {item.thumbnail ? (
                        <div className="w-full h-16 rounded-md mb-1.5 overflow-hidden bg-neutral-100">
                          {item.thumbnail.startsWith('linear-gradient') ? (
                            <div
                              className="w-full h-full"
                              style={{ background: item.thumbnail }}
                            />
                          ) : (
                            <img
                              src={item.thumbnail}
                              alt={item.label}
                              className="w-full h-full object-cover"
                              referrerPolicy="no-referrer"
                            />
                          )}
                        </div>
                      ) : (
                        <div className={`w-full h-16 rounded-md mb-1.5 flex items-center justify-center ${
                          item.source_type === 'video' ? 'bg-gradient-to-br from-red-100 to-red-200' :
                          item.source_type === 'image' ? 'bg-gradient-to-br from-blue-100 to-blue-200' :
                          item.source_type === 'link' ? 'bg-gradient-to-br from-green-100 to-green-200' :
                          item.source_type === 'note' ? 'bg-gradient-to-br from-yellow-100 to-yellow-200' :
                          'bg-gradient-to-br from-purple-100 to-purple-200'
                        }`}>
                          <span className={`text-lg font-bold ${
                            item.source_type === 'video' ? 'text-red-600' :
                            item.source_type === 'image' ? 'text-blue-600' :
                            item.source_type === 'link' ? 'text-green-600' :
                            item.source_type === 'note' ? 'text-yellow-600' :
                            'text-purple-600'
                          }`}>
                            {item.source_type === 'video' ? '▶' :
                             item.source_type === 'image' ? '🖼' :
                             item.source_type === 'link' ? '🔗' :
                             item.source_type === 'note' ? '📝' :
                             item.label.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}

                      {/* Label */}
                      <div className="text-xs font-medium text-neutral-900 truncate leading-tight">
                        {item.label}
                      </div>

                      {/* Checkmark */}
                      {selected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-1.5 right-1.5 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center"
                        >
                          <Check size={12} className="text-white" />
                        </motion.div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {!loading && getCurrentData().length === 0 && (
              <div className="flex items-center justify-center h-full text-neutral-500 text-sm">
                No {selectorTab} found
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-neutral-200/50">
            <div className="text-sm text-neutral-600">
              {selectorSelected.length} selected
            </div>
            <div className="flex gap-2">
              <button
                onClick={closeSelector}
                className="px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmSelectorSelection}
                disabled={selectorSelected.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Add to Context
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

